import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {SourceDocument} from '@github-ui/taillights-worker/document'
import {createContext, startTransition, use, useEffect, useMemo, useReducer, useState, type ReactNode} from 'react'
import {createTaillightsCSSScope} from './taillight-css'
import {TaillightsController} from './taillights-controller'
import {resolveTaillightsAppearance, type TaillightsRenderingOptions} from './taillights-rendering-styles'

const TaillightsControllerContext = createContext<TaillightsController | null>(null)
const TaillightsRenderingContext = createContext<TaillightsRenderingOptions | undefined>(undefined)
const TaillightsCSSContext = createContext<ReturnType<typeof createTaillightsCSSScope> | null>(null)

export type TaillightsProviderProps = {
  source: SourceDocument
  rendering?: TaillightsRenderingOptions
  appearanceKey?: string
  onError?: (error: Error) => void
  children: ReactNode
}

type Configuration = Pick<TaillightsProviderProps, 'source' | 'rendering' | 'appearanceKey'>
type Connection = {
  configuration: Configuration
  controller: TaillightsController | null
  error: Error | null
}

function connectionReducer(_state: Connection | null, connection: Connection) {
  return connection
}

/** Turns an asynchronous connection failure into a render error for the shared ErrorBoundary. */
function ConnectionError({error}: {error: Error}): never {
  throw error
}

/**
 * Resolves shared appearance and owns a worker connection, exposing it only after initialization succeeds.
 * Replaces and disposes the connection when the source or rendering configuration changes.
 */
export function TaillightsProvider({source, rendering, appearanceKey, onError, children}: TaillightsProviderProps) {
  const {font, lineHeight, paddingInline, tabSize, defaultColor, colors} = rendering ?? {}
  const options = useMemo(
    () => ({font, lineHeight, paddingInline, tabSize, defaultColor, colors}),
    [font, lineHeight, paddingInline, tabSize, defaultColor, colors],
  )
  const configuration = useMemo(() => ({source, rendering: options, appearanceKey}), [source, options, appearanceKey])
  const [connection, dispatch] = useReducer(connectionReducer, null)
  const [cssScope] = useState(createTaillightsCSSScope)

  useEffect(() => () => cssScope.dispose(), [cssScope])

  useEffect(() => {
    let active = true
    let controller: TaillightsController | null = null
    async function connect() {
      try {
        const appearance = await resolveTaillightsAppearance(document.body, configuration.rendering)
        if (!active) return
        controller = new TaillightsController(configuration.source, {
          font: appearance.font,
          fonts: appearance.fonts,
          lineHeight: appearance.lineHeight,
          paddingInline: appearance.paddingLeft,
          tabSize: configuration.rendering?.tabSize ?? 4,
          defaultColor: appearance.defaultColor,
          colors: appearance.colors,
        })
        await controller.ready
        if (!active) return
        startTransition(() => dispatch({configuration, controller, error: null}))
        const error = await controller.failure
        if (active) startTransition(() => dispatch({configuration, controller: null, error}))
      } catch (error) {
        if (active) {
          const failure = error instanceof Error ? error : new Error(String(error))
          startTransition(() =>
            dispatch({
              configuration,
              controller: null,
              error: failure,
            }),
          )
        }
      }
    }
    void connect()
    return () => {
      active = false
      controller?.dispose()
    }
  }, [configuration])

  const current = connection?.configuration === configuration ? connection : null
  const controller = current?.controller
  return (
    <TaillightsRenderingContext value={options}>
      <TaillightsControllerContext value={controller && !controller.disposed ? controller : null}>
        <TaillightsCSSContext value={cssScope}>{children}</TaillightsCSSContext>
        {current?.error ? (
          <ErrorBoundary boundaryName="TaillightsProvider" fallback={null} onError={onError}>
            <ConnectionError error={current.error} />
          </ErrorBoundary>
        ) : null}
      </TaillightsControllerContext>
    </TaillightsRenderingContext>
  )
}

export function useTaillightsController(): TaillightsController | null {
  return use(TaillightsControllerContext)
}

export function useTaillightsRendering() {
  return use(TaillightsRenderingContext)
}

/** Shares DOM highlight bindings within a provider without publishing paint readiness through context. */
export function useTaillightsCSSScope() {
  const scope = use(TaillightsCSSContext)
  if (!scope) throw new Error('CSS highlighting requires a TaillightsProvider.')
  return scope
}
